import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LgmButton from '../LgmButton';
import Typography from '@mui/material/Typography';
import { PutProductConflict } from '../../services/api/domains/products/types';
import { useForkProduct } from '../../services/api/domains/products/hooks';
import { GRAY_200, GRAY_50 } from 'theme/grayScale';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';

interface ConflictModalProps {
  conflict: PutProductConflict;
  originalProductId: number;
  manufacturerId: number;
  newManProductName?: string | null;
  currentProductName: string;
  currentCasNumber?: string | null;
  currentManProductName?: string | null;
  onForkSuccess: () => void;
  onCancel: () => void;
}

/**
 * ConflictModal — Task 5.1 (FR-3a)
 *
 * Shown when PUT /products/:id returns status:'conflict': the product's new
 * man_product_name resolves to a DIFFERENT single CAS entity in the registry.
 * Offers Cancel (no change) or fork — creating a new product record for the
 * conflicting registry entry and moving this manufacturer's link to it.
 *
 * Renders with zIndex 20000 to stack above the ProductModal overlay (z-index 10000).
 */
const ConflictModal: React.FC<ConflictModalProps> = ({
  conflict,
  originalProductId,
  manufacturerId,
  newManProductName,
  currentProductName,
  currentCasNumber,
  currentManProductName,
  onForkSuccess,
  onCancel,
}) => {
  const forkMutation = useForkProduct();
  const isForkPending = forkMutation.status === 'loading';

  // Back-button dismiss: stacks correctly on top of ProductModal (both use
  // the same shared modal stack) and respects the existing isForkPending
  // close-disable state, mirroring the `onClose={isForkPending ? undefined
  // : onCancel}` MUI wiring below.
  useModalBackDismiss({
    isOpen: true,
    onClose: onCancel,
    canDismiss: () => !isForkPending,
  });

  const forkButtonLabel =
    conflict.orphanCount === 1
      ? 'Create as New Product + Delete Original'
      : 'Create as New Product';

  const handleFork = async () => {
    try {
      await forkMutation.mutate({
        productId: originalProductId,
        data: {
          manufacturerId,
          conflictingEntity: conflict.conflictingEntity,
          newManProductName: newManProductName ?? undefined,
        },
      });
      onForkSuccess();
    } catch (err) {
      console.error('❌ Fork failed:', err);
      alert('Failed to create new product. Please try again.');
    }
  };

  return (
    <Dialog
      open
      onClose={isForkPending ? undefined : onCancel}
      sx={{ zIndex: 20000 }}
      slotProps={{ paper: { sx: { minWidth: 440, maxWidth: 560 } } }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Registry Conflict Detected
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          The new name resolves to a <strong>different chemical entity</strong>{' '}
          in the registry than this product&apos;s current CAS registration.
        </Typography>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              background: GRAY_50,
              border: `1px solid ${GRAY_200}`,
              borderRadius: 6,
              padding: '12px 16px',
            }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                mb: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
              Current product
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {currentProductName}
            </Typography>
            {currentCasNumber && (
              <Typography variant="body2" color="text.secondary">
                CAS {currentCasNumber}
              </Typography>
            )}
            {currentManProductName && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.75 }}>
                Manufacturer name: {currentManProductName}
              </Typography>
            )}
          </div>

          <div
            style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 6,
              padding: '12px 16px',
            }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                mb: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
              New registry match
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {conflict.conflictingEntity.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CAS {conflict.conflictingEntity.rn}
            </Typography>
          </div>
        </div>

        {conflict.orphanCount === 1 && (
          <div
            style={{
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              borderRadius: 6,
              padding: '10px 16px',
            }}>
            <Typography variant="body2" color="warning.dark">
              <strong>The original product will be permanently deleted.</strong>{' '}
              This is its only manufacturer link — once moved to the new
              product, nothing will remain and it will be removed automatically.
            </Typography>
          </div>
        )}

        <Typography variant="body2">
          <strong>Create as New Product</strong> will create a new product
          record for this registry entry and move this manufacturer&apos;s link
          to it. The current product record remains unchanged (unless it would
          be left with no manufacturer links, in which case it is removed
          automatically).
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: '12px 16px', gap: 1 }}>
        <LgmButton variant="flat" onClick={onCancel} disabled={isForkPending}>
          Cancel
        </LgmButton>
        <Button
          onClick={handleFork}
          loading={isForkPending}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: '#059669',
            '&:hover': { backgroundColor: '#047857' },
          }}>
          {forkButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConflictModal;
